import Dialog from "@mui/material/Dialog";
import { AlertType, ApplicationScreen, Clinic, Patient } from "../../models";
import CircularProgress from "@mui/material/CircularProgress";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiAccessRequest } from "../../constants";
import { useAlertContext, useUserContext } from "../../contexts";
import { useMsal } from "@azure/msal-react";
import { useCreateAuditLog } from "../../hooks/useCreateAuditLog";

interface ConfirmAddBannedPatientProps {
	patient: Patient;
	clinic?: Clinic;
	open: boolean;
	onClose: (updated: boolean) => void;
}

export const ConfirmAddBannedPatient = ({
	patient,
	clinic,
	open,
	onClose
}: ConfirmAddBannedPatientProps) => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const { showAlert } = useAlertContext();
	const { createAuditLog } = useCreateAuditLog();
	const [isError, setIsError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const notesEl = useRef<HTMLTextAreaElement | null>(null);

	useEffect(() => {
		setIsError(false);
		setIsLoading(false);
	}, [patient]);

	const createAuditEntry = useCallback(
		async (patient: Patient) => {
			try {
				await createAuditLog([
					{
						clinicId: clinic?.id,
						username: account?.name ?? "",
						applicationScreen: ApplicationScreen.AdminBannedPatient,
						action: `Added banned patient id:${patient.bpId} name: ${patient.firstName}  ${patient.surname}`
					}
				]);
			} catch (error) {
				console.log("Error creating audit log", error);
			}
		},
		[account?.name, clinic?.id, createAuditLog]
	);

	const handleConfirmClick = async () => {
		setIsError(false);
		setIsLoading(true);
		try {
			const notes = notesEl.current?.value;

			const requestUrl = `${process.env.REACT_APP_CONSULTS_URL}patient/bannedpatient`;
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`,
				"Content-Type": "application/json"
			};
			const body = { patientId: patient?.id, notes: notes ?? "" };
			const response = await fetch(requestUrl, {
				method: "POST",
				headers,
				body: JSON.stringify(body)
			});

			console.info("response", response);
			if (!response.ok) {
				const data = await response.json();
				console.info("data", data);
				const message =
					data.exceptionDetails?.[0]?.message ??
					data.detail ??
					data.title ??
					"";
				if (message !== "") {
					showAlert({
						message,
						alertType: AlertType.error
					});
					return;
				}
				throw new Error(message);
			}
			await createAuditEntry(patient);
			onClose(true);
			showAlert({
				message: `${patient.firstName} ${patient.surname} has been added to banned patients`,
				alertType: AlertType.success,
				timeout: true
			});
		} catch (error) {
			console.info("error", error);
			setIsError(true);
			showAlert({
				message: (error as any).message
					? (error as any).message
					: `There was an error adding ${patient.firstName} ${patient.surname} to banned patients`,
				alertType: AlertType.error
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={open}>
			<div
				className="flex flex-col items-center p-5 gap-4"
				style={{ width: 500 }}
			>
				{!isError && (
					<>
						<p className="ch-m-text ch-em-text-navy text-center">
							Add Banned Patient
						</p>
						<p className="pb-3 text-center">
							You are going to add {patient.firstName} {patient.surname} as a
							banned patient.
						</p>

						<p className="w-full pb-3">
							Notes
							<textarea
								className="ch-border w-full"
								style={{ resize: "none", padding: "4px 12px" }}
								rows={4}
								ref={notesEl}
							/>
						</p>
						<div className="flex gap-4">
							{!isLoading && (
								<>
									<button
										className="ch-button ch-button-primary justify-center"
										onClick={() => onClose(false)}
									>
										Go Back
									</button>
									<button
										className="ch-button ch-button-secondary justify-center"
										onClick={() => handleConfirmClick()}
									>
										Confirm
									</button>
								</>
							)}
							{isLoading && (
								<div className="flex justify-center py-3">
									<CircularProgress size="80px" />
								</div>
							)}
						</div>
					</>
				)}
				{isError && (
					<div className="flex flex-col items-center py-3">
						<img
							src="/images/svg/road-sign-warning.svg"
							height="50"
							alt="error icon"
						/>
						<p className="ch-m-text py-3 text-center">
							An error has occurred. The patient has not been added to the
							banned patients. Try again later
						</p>
						<div className="flex gap-6">
							<button
								className="ch-button ch-button-primary"
								onClick={() => onClose(false)}
							>
								Go back
							</button>
						</div>
					</div>
				)}
			</div>
		</Dialog>
	);
};
